// import React from 'react'
import {useMatches} from '@remix-run/react';

//components
import SanityImage from 'components/SanityImage';
import {Link} from 'components/Link';

//types
import type {SanityModuleHeroBanner} from 'domains/modules';
import {I18nLocale} from '~/lib/type';
interface Props {
  module: SanityModuleHeroBanner;
  /**
   * Inside the carousel the parent slide fixes the height (a uniform
   * aspect-ratio box), so the banner fills it with object-cover — that keeps
   * every slide the exact same height and stops the carousel from jumping /
   * cropping / needing a scroll as images load. Standalone (single banner)
   * keeps its natural responsive height, with the real aspect ratio reserved
   * up front so there is no load-time shift.
   */
  fill?: boolean;
  /**
   * The banner is the largest thing in the first viewport, so it is the LCP
   * element on the home page. Nothing marked it as such: the browser found
   * it at its normal place in the queue behind fonts and third-party
   * scripts. A standalone banner is always first; in the carousel only the
   * first slide is visible at load, so only it gets the hint.
   */
  priority?: boolean;
}

export default function HeroBanner({module, fill, priority = !fill}: Props) {
  const [root] = useMatches();
  const selectedLocale = root?.data?.selectedLocale as I18nLocale;
  const banner_ref =
    selectedLocale.language === 'ZH_TW'
      ? module.banner_image?.asset._ref
      : module.banner_image_en?.asset._ref;
  // Pull the asset's pixel dimensions out of its Sanity _ref
  // (image-<hash>-<W>x<H>-<ext>) so the standalone <img> can reserve its real
  // aspect ratio from the first paint (no load-time shift).
  const dims = banner_ref?.match(/-(\d+)x(\d+)-\w+$/);
  const bannerWidth = dims ? Number(dims[1]) : undefined;
  const bannerHeight = dims ? Number(dims[2]) : undefined;
  const link =
    selectedLocale.language === 'ZH_TW' ? module.link : module.link_en;
  // 45 of the home page's 85 images shipped without alt; the banners were
  // most of them. Prefer the alt an editor wrote on the image itself; fall
  // back to the banner title unless it is the untouched "Hero Banner"
  // placeholder every module starts with — a banner that says nothing is
  // decorative, and an empty alt is the honest value for that.
  const image =
    selectedLocale.language === 'ZH_TW'
      ? module.banner_image
      : module.banner_image_en ?? module.banner_image;
  const title =
    selectedLocale.language === 'ZH_TW'
      ? module.banner_title?.zh_TW
      : module.banner_title?.en ?? module.banner_title?.zh_TW;
  const isPlaceholderTitle = /^hero banner\.?$/i.test(title?.trim() ?? '');
  const alt =
    (image as {alt?: string} | undefined)?.alt?.trim() ||
    (title && !isPlaceholderTitle ? title.trim() : '');
  const priorityProps = priority
    ? {loading: 'eager' as const, fetchpriority: 'high'}
    : {loading: 'lazy' as const};

  const banner = fill ? (
    <SanityImage
      layout="fill"
      objectFit="cover"
      src={banner_ref}
      className="w-full"
      alt={alt}
      {...priorityProps}
    />
  ) : (
    <SanityImage
      layout="responsive"
      width={bannerWidth}
      height={bannerHeight}
      src={banner_ref}
      className="w-full object-cover"
      alt={alt}
      {...priorityProps}
    />
  );

  const sectionClassName = fill
    ? 'module--hero-banner relative h-full w-full'
    : 'module--hero-banner w-full';
  // The link wrappers must pass the fixed height down to the section so the
  // layout=fill image (position:absolute) has a sized parent to fill.
  const linkClassName = fill ? 'block h-full' : undefined;

  const section = <section className={sectionClassName}>{banner}</section>;

  if (link) {
    if (link._type === 'linkInternal') {
      return (
        <Link to={`${link.slug}`} className={linkClassName} key={module._key}>
          {section}
        </Link>
      );
    }
    if (link._type === 'linkExternal') {
      return (
        <a
          href={link.url}
          target={link.newWindow ? '_blank' : '_self'}
          rel="noreferrer noopener"
          className={linkClassName}
          key={module._key}
        >
          {section}
        </a>
      );
    }
  }
  return section;
}
